import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import Accordion from '@mui/joy/Accordion'
import AccordionDetails from '@mui/joy/AccordionDetails'
import AccordionSummary from '@mui/joy/AccordionSummary'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getGarageParkingEndMessage, type GarageParkingPeriod } from '@/garage-parking-period'

export default function ReservationExtensionAlert({
    parkingPeriod,
    mobile = false,
}: {
    parkingPeriod: GarageParkingPeriod,
    mobile?: boolean,
}) {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)

    return (
        <Accordion expanded={expanded} onChange={(_event, nextExpanded) => setExpanded(nextExpanded)} sx={{
            border: '1px solid var(--joy-palette-neutral-100)',
            borderRadius: 10,
            backgroundColor: mobile ? 'white' : undefined,
            color: 'customGreen.dark',
            overflow: 'hidden',
            '--ListItem-paddingY': '0px',
            '--ListItem-paddingX': '0px',
            ...(mobile ? {
                boxShadow: `var(--joy-shadowRing, 0 0 #000),
                    0.3px 0.8px 1.1px rgba(187 187 187 / 0.11),
                    0.5px 1.3px 1.8px -0.6px rgba(187 187 187 / 0.18),
                    1.1px 2.7px 3.8px -1.2px rgba(187 187 187 / 0.26)`,
            } : {}),
        }}>
            <AccordionSummary
                indicator={<ExpandMoreRoundedIcon sx={{ fontSize: 24 }} />}
                slotProps={{ button: { sx: {
                    px: 1.5,
                    py: mobile ? 1 : 1.5,
                    gap: 1.25,
                    '&& svg': { fill: 'currentColor' },
                    '&&:hover svg': { fill: 'currentColor' },
                    ...(mobile ? { '&&:hover': { backgroundColor: 'white' } } : {}),
                    '&&:focus, &&:active:focus': { boxShadow: 'none' },
                } }, indicator: { sx: { transition: 'transform 0.2s ease' } } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <AccessTimeRoundedIcon sx={{ fontSize: 24, color: 'customGreen.dark', flexShrink: 0 }} />
                    <Typography component="span" level={mobile ? 'body-xs' : 'title-sm'} sx={{
                        color: 'customGreen.dark', fontWeight: 600, textAlign: 'left',
                    }}>
                        {t('checkout.extensionAlert.heading', { count: parkingPeriod.durationHours })}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{ '--AccordionDetails-transition': 'grid-template-rows 0.2s ease, padding-block 0.2s ease' }}
                slotProps={{
                    root: { hidden: false, 'aria-hidden': !expanded },
                    content: { sx: { px: 1.5, py: expanded ? 1.5 : 0 } },
                }}
            >
                <Typography component="p" level="body-xs" sx={{ color: 'customGreen.dark', textAlign: 'left' }}>
                    {t('minimumStayDetail.minimum', { count: parkingPeriod.durationHours })}{' '}
                    {t('checkout.extensionAlert.explanation')}{' '}
                    {getGarageParkingEndMessage(parkingPeriod, t, 'checkout.extensionAlert')}
                </Typography>
            </AccordionDetails>
        </Accordion>
    )
}
